﻿import React, { useEffect } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, Layout,
  BlockStack, InlineStack, List, Divider, Box, Spinner,
  Modal,
} from '@shopify/polaris';
import { CreditCardIcon } from '@shopify/polaris-icons';
import { useBilling, usePlans } from '../../hooks/useBilling';
import type { Plan } from '../../api/billing';
import { IconBadge } from '../shared/IconBadge';

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusBadge(status: string) {
  const map: Record<string, 'success' | 'warning' | 'critical' | 'info'> = {
    active: 'success', pending: 'warning', declined: 'critical', cancelled: 'critical',
  };
  return <Badge tone={map[status] ?? 'info'}>{status}</Badge>;
}

// ── Plan Card ─────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: Plan;
  onSubscribe: (planId: number) => void;
  subscribing: number | null;
  isCurrentActive: boolean;
}

function PlanCard({ plan, onSubscribe, subscribing, isCurrentActive }: PlanCardProps) {
  const isCurrent     = plan.is_current;
  const isLoading     = subscribing === plan.id;
  const isComingSoon  = !plan.is_free && !isCurrent && !plan.is_available_for_signup;
  const canSubscribe  = !plan.is_free && !isCurrent && plan.is_available_for_signup;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" gap="200">
          <Text as="h2" variant="headingMd">{plan.name}</Text>
          {isCurrent && <Badge tone="success">Current Plan</Badge>}
          {isComingSoon && <Badge tone="info">Coming Soon</Badge>}
        </InlineStack>

        <Text as="p" variant="bodyMd" tone="subdued">{plan.description}</Text>

        <BlockStack gap="100">
          <InlineStack gap="100" align="start" blockAlign="baseline">
            <Text as="span" variant="heading2xl" fontWeight="bold">
              {plan.is_free ? 'Free' : plan.price_monthly_display}
            </Text>
            {!plan.is_free && (
              <Text as="span" variant="bodyMd" tone="subdued">/month</Text>
            )}
          </InlineStack>
          {!plan.is_free && plan.savings_pct > 0 && (
            <Text as="p" variant="bodySm" tone="success">
              Save {plan.savings_pct}% with annual billing ({plan.price_yearly_display}/year)
            </Text>
          )}
        </BlockStack>

        <Divider />

        {plan.features.length > 0 && (
          <List type="bullet">
            {plan.features.map((f, i) => (
              <List.Item key={i}>
                {f.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </List.Item>
            ))}
          </List>
        )}

        {canSubscribe ? (
          <Button
            variant="primary"
            fullWidth
            loading={isLoading}
            disabled={subscribing !== null && !isLoading}
            onClick={() => onSubscribe(plan.id)}
          >
            {isCurrentActive ? 'Switch Plan' : 'Upgrade'}
          </Button>
        ) : isComingSoon ? (
          <Button fullWidth disabled>Coming Soon</Button>
        ) : plan.is_free ? (
          <Button fullWidth disabled>Free Forever</Button>
        ) : (
          <Button fullWidth disabled>Current Plan</Button>
        )}
      </BlockStack>
    </Card>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

export function BillingPanel() {
  const { plans, loading: plansLoading, fetchPlans } = usePlans();
  const {
    charge, loading: billingLoading, subscribing, cancelling, error,
    fetchStatus, subscribe, cancel, setError,
  } = useBilling();

  const [showCancelModal, setShowCancelModal] = React.useState(false);

  useEffect(() => {
    fetchPlans();
    fetchStatus();
  }, []);

  const loading = plansLoading || billingLoading;

  return (
    <Page fullWidth
      title="Billing & Plans"
      subtitle="Manage your GrowthPilot AI subscription"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {/* Current subscription status */}
          {charge && (
            <Card>
              <BlockStack gap="300">
                <InlineStack gap="300" blockAlign="center" wrap={false}>
                  <IconBadge icon={CreditCardIcon} color="indigo" />
                  <Text as="h2" variant="headingMd">Current Subscription</Text>
                </InlineStack>
                <InlineStack gap="300" align="start">
                  {statusBadge(charge.status)}
                  {charge.is_active && charge.billing_on && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      Next billing: {new Date(charge.billing_on).toLocaleDateString()}
                    </Text>
                  )}
                  {charge.trial_days > 0 && charge.status === 'active' && !charge.billing_on && (
                    <Text as="span" variant="bodySm" tone="success">
                      Trial active ({charge.trial_days} days)
                    </Text>
                  )}
                </InlineStack>

                {charge.status === 'pending' && charge.confirmation_url && (
                  <Banner tone="warning">
                    <p>Your subscription is pending approval.</p>
                    <Button
                      url={charge.confirmation_url}
                      external
                      variant="plain"
                    >
                      Complete approval on Shopify
                    </Button>
                  </Banner>
                )}

                {charge.is_active && (
                  <Box>
                    <Button
                      tone="critical"
                      loading={cancelling}
                      onClick={() => setShowCancelModal(true)}
                    >
                      Cancel Subscription
                    </Button>
                  </Box>
                )}
              </BlockStack>
            </Card>
          )}

          {/* Plans grid */}
          {loading ? (
            <Card>
              <InlineStack align="center"><Spinner /></InlineStack>
            </Card>
          ) : plans.length === 0 ? (
            <Card>
              <Text as="p" tone="subdued">Unable to load available plans. Try refreshing the page.</Text>
            </Card>
          ) : (
            <>
              <Text as="h2" variant="headingMd">Available Plans</Text>
              <InlineStack gap="400" wrap>
                {plans.map((plan) => (
                  <Box key={plan.id} minWidth="280px" maxWidth="340px">
                    <PlanCard
                      plan={plan}
                      onSubscribe={subscribe}
                      subscribing={subscribing}
                      isCurrentActive={!!charge?.is_active}
                    />
                  </Box>
                ))}
              </InlineStack>
            </>
          )}
          </BlockStack>
        </Layout.Section>
      </Layout>

      <Modal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
        primaryAction={{
          content: 'Yes, Cancel',
          destructive: true,
          loading: cancelling,
          onAction: async () => {
            await cancel();
            setShowCancelModal(false);
          },
        }}
        secondaryActions={[{
          content: 'Keep Subscription',
          onAction: () => setShowCancelModal(false),
        }]}
      >
        <Modal.Section>
          <Text as="p" variant="bodyMd">
            Are you sure you want to cancel your subscription? You will be downgraded to the
            Free plan immediately and lose access to paid features.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
